import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { classNames } from '../../utilities/styleUtils';

const buttonConfig: {
  variants: {
    intent: {
      primary: string[];
      secondary: string[];
      danger: string[];
      warning: string[];
      subtle: string[];
      light: string[];
      secondaryAction: string[];
      custom: string[];
    };
    size: {
      small: string[];
      medium: string[];
      mediumPlus: string[];
      large: string[];
    };
  };
  defaultVariants: {
    intent: 'primary' | 'secondary' | 'danger' | 'warning' | 'subtle' | 'light' | 'secondaryAction' | 'custom';
    size: 'small' | 'medium' | 'mediumPlus' | 'large';
  };
} = {
  variants: {
    intent: {
      primary: [
        'bg-dataops-yale-blue',
        'text-white',
        'shadow-sm',
        'hover:bg-dataops-yale-blue',
        'disabled:bg-gray-300',
      ],
      secondary: [
        'bg-dataops-process-cyan',
        'text-white',
        'shadow-sm',
        'hover:bg-dataops-process-cyan',
        'disabled:bg-gray-300',
      ],
      danger: ['bg-[#ec6a46]', 'text-white', 'shadow-sm', 'hover:bg-red-600', 'disabled:bg-[#ec6a46]/70'],
      warning: [
        'bg-white',
        'text-dataops-rich-black',
        'shadow-sm',
        'ring-1',
        'ring-gray-300',
        'hover:bg-dataops-saffron-yellow',
        'disabled:bg-gray-400/70',
      ],
      subtle: ['bg-white', 'text-gray-900', 'ring-0', 'shadow-none', 'hover:underline'],
      light: [
        'bg-white',
        'text-gray-900',
        'shadow-sm',
        'ring-1',
        'ring-gray-300',
        'hover:bg-gray-200',
        'disabled:bg-gray-300',
        'disabled:hover:!bg-gray-300',
        'disabled:text-white',
      ],
      secondaryAction: [
        'bg-dataops-yale-blue/80',
        'text-white',
        'hover:bg-action-dataops-saffron-yellow',
        'disabled:bg-gray-500',
        'shadow-sm',
        'w-full',
      ],
      custom: [],
    },
    size: {
      small: ['text-xs', 'py-0.5', 'px-1'],
      medium: ['text-sm', 'py-1', 'px-2', 'h-6'],
      mediumPlus: ['text-sm', 'py-1', 'px-2', 'h-8'],
      large: ['text-sm', 'py-2', 'px-3.5'],
    },
  },
  defaultVariants: {
    intent: 'primary',
    size: 'medium',
  },
};

const button = cva('button', buttonConfig);
const link = cva('a', buttonConfig);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  url?: string;
  loading?: boolean;
}

const Button: React.FC<ButtonProps> = ({ url, className, intent, size, loading = false, disabled, ...props }) => {
  if (url !== undefined) {
    return (
      <a
        href={url}
        target={'_blank'}
        rel="noreferrer"
        className={classNames(
          'font-normal rounded-md flex items-center text-nowrap',
          link({ intent, size, className }),
        )}
      >
        {props.children}
      </a>
    );
  }

  return (
    <button
      className={classNames(
        'font-normal rounded-md flex items-center text-nowrap',
        'relative',
        button({ intent, size, className }),
        loading ? 'cursor-wait' : '',
      )}
      disabled={disabled ?? loading}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-inherit rounded-md">
          <div className="h-2 w-2 animate-spin rounded-full border-2 border-current border-t-transparent opacity-75" />
        </div>
      )}
      <div className={classNames(loading ? 'invisible' : 'flex items-center')}>{props.children}</div>
    </button>
  );
};

export default Button;
